/**
 * Client-side AES-256-GCM unwrap/wrap for admin API payloads.
 * Must stay in sync with zunServer/utilities/adminPayloadCrypto.js
 *
 * Local (localhost API): plaintext request/response for easy debugging.
 * UAT / Production: encrypt so Network tab does not show DB-shaped JSON.
 */
const ENVELOPE_VERSION = 1;
const ENVELOPE_MARKER = 'z0';
const IV_LENGTH = 12;
const AUTH_TAG_LENGTH = 16;

const resolveSecret = () => {
  const fromEnv = String(process.env.REACT_APP_ADMIN_API_PAYLOAD_SECRET || '').trim();
  if (fromEnv) return fromEnv;
  return 'zuntly-admin-portal-payload-v1';
};

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const base64ToBytes = (value) => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const bytesToBase64 = (bytes) => {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
};

const sha256Bytes = async (input) => {
  const digest = await crypto.subtle.digest('SHA-256', textEncoder.encode(input));
  return new Uint8Array(digest);
};

let cachedKeyPromise = null;
const getCryptoKey = () => {
  if (!cachedKeyPromise) {
    cachedKeyPromise = sha256Bytes(resolveSecret()).then((raw) => (
      crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
    ));
  }
  return cachedKeyPromise;
};

/** True only for UAT/Production builds (or explicit flag). Local localhost stays off. */
export const isAdminPayloadProtectionEnabled = () => {
  const flag = String(process.env.REACT_APP_ADMIN_API_PAYLOAD_ENC || '').trim().toLowerCase();
  if (flag === '1' || flag === 'true' || flag === 'on' || flag === 'yes') return true;
  if (flag === '0' || flag === 'false' || flag === 'off' || flag === 'no') return false;

  const stage = String(process.env.REACT_APP_APP_STAGE || '').trim().toLowerCase();
  if (['uat', 'play', 'staging', 'prod', 'production'].includes(stage)) return true;

  const api = String(process.env.REACT_APP_API_URL || '');
  try {
    const host = new URL(api).hostname;
    if (!host || host === 'localhost' || host === '127.0.0.1') return false;
    return true;
  } catch {
    return Boolean(api) && !api.includes('localhost') && !api.includes('127.0.0.1');
  }
};

export const isAdminPayloadEnvelope = (value) => (
  value
  && typeof value === 'object'
  && !Array.isArray(value)
  && value[ENVELOPE_MARKER] === ENVELOPE_VERSION
  && typeof value.p === 'string'
  && typeof value.n === 'string'
);

export const unwrapAdminPayload = async (value) => {
  // Always unwrap if an envelope arrives (safe if UAT response hits a mixed client).
  if (!isAdminPayloadEnvelope(value)) return value;
  const key = await getCryptoKey();
  const iv = base64ToBytes(value.n);
  const packed = base64ToBytes(value.p);
  if (packed.length <= AUTH_TAG_LENGTH) {
    throw new Error('Invalid protected payload');
  }
  const data = packed.subarray(0, packed.length - AUTH_TAG_LENGTH);
  const tag = packed.subarray(packed.length - AUTH_TAG_LENGTH);
  const cipherBytes = new Uint8Array(data.length + tag.length);
  cipherBytes.set(data, 0);
  cipherBytes.set(tag, data.length);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: AUTH_TAG_LENGTH * 8 },
    key,
    cipherBytes,
  );
  return JSON.parse(textDecoder.decode(decrypted));
};

export const wrapAdminPayload = async (payload) => {
  if (!isAdminPayloadProtectionEnabled()) return payload ?? {};
  const key = await getCryptoKey();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const plaintext = textEncoder.encode(JSON.stringify(payload ?? {}));
  const encrypted = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: AUTH_TAG_LENGTH * 8 },
    key,
    plaintext,
  ));
  // WebCrypto appends auth tag at the end — matches Node packed format.
  return {
    [ENVELOPE_MARKER]: ENVELOPE_VERSION,
    n: bytesToBase64(iv),
    p: bytesToBase64(encrypted),
  };
};

/** Parse response JSON and transparently decrypt protected admin envelopes. */
export const parseAdminJsonResponse = async (response) => {
  const data = await response.json();
  return unwrapAdminPayload(data);
};
